import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { Paper, CircularProgress, TextField } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AuthService from "../../api/AuthService";
import { useAlert } from "../../context/AlertContext";
import styles from "../../entities/ResetPasswordPage/styles";
import { refreshDateFormat } from "../../utils/dateFormatStore";

// Handles the link the user clicks in the verification email:
// ${FRONTEND_URL}/verify-email/{token}. Verifies the token, swaps the stored
// JWT for the fresh one, then moves the user on to phone verification.
const VerifyToken = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const { showAlert } = useAlert();

  const [status, setStatus] = useState("verifying"); // verifying | error
  const [resending, setResending] = useState(false);
  // Prefer the logged-in email; fall back to a manual entry if we don't have it
  // (e.g. the link was opened in a fresh browser).
  const [email, setEmail] = useState(localStorage.getItem("email") || "");

  // StrictMode double-invokes effects in dev — guard so we only verify once.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    const verify = async () => {
      if (!token) {
        setStatus("error");
        return;
      }
      try {
        const result = await AuthService.verifyEmail(token);
        if (result?.success && result?.data?.isEmailVerified) {
          // The old token still reads as unverified — replace it.
          if (result?.data?.token) {
            localStorage.setItem("accessToken", result.data.token);
            refreshDateFormat();
          }
          localStorage.setItem("isEmailVerified", "true");
          if (typeof result?.data?.isPhoneVerified !== "undefined") {
            localStorage.setItem("isPhoneVerified", result.data.isPhoneVerified);
          }
          if (result?.data?.email) {
            localStorage.setItem("email", result.data.email);
          }
          showAlert("Your email has been verified.", "success");

          const phoneVerified =
            localStorage.getItem("isPhoneVerified") === "true";
          const loggedIn = !!localStorage.getItem("accessToken");
          // Continue to phone verification when we have a session and the
          // phone still needs verifying; otherwise fall back to login.
          navigate(loggedIn && !phoneVerified ? "/verify-phone" : "/dashboard");
        } else {
          setStatus("error");
        }
      } catch (error) {
        setStatus("error");
      }
    };

    verify();
  }, [token, navigate, showAlert]);

  const handleResend = async () => {
    setResending(true);
    try {
      await AuthService.resendVerificationEmail(email);
      showAlert(
        "If an account exists for that email, a new verification link is on its way.",
        "success"
      );
    } catch (error) {
      showAlert(error?.message || "Something went wrong. Please try again.", "error");
    } finally {
      setResending(false);
    }
  };

  return (
    <Box sx={styles.main}>
      <Container sx={styles.root} maxWidth="sm">
        <Paper sx={styles.paper}>
          <Grid container sx={styles.gridContainer}>
            <Grid item xs={12} sx={styles.gridItem}>
              <Box sx={styles.formContain}>
                {status === "verifying" ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <CircularProgress sx={{ color: "#000", mb: 2 }} />
                    <Typography sx={styles.descriptionText}>
                      Verifying your email…
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Typography variant="h4" sx={styles.mainheading}>
                      Verification failed
                    </Typography>
                    <Box sx={styles.headingborder}></Box>
                    <Typography sx={styles.descriptionText}>
                      Invalid or expired verification link. Please request a new
                      one.
                    </Typography>

                    {!localStorage.getItem("email") && (
                      <Box sx={{ mb: 1 }}>
                        <Typography sx={styles.inputText}>Email</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@example.com"
                        />
                      </Box>
                    )}

                    <LoadingButton
                      loading={resending}
                      onClick={handleResend}
                      fullWidth
                      variant="contained"
                      disabled={!email}
                      sx={styles.submit}
                    >
                      Resend verification email
                    </LoadingButton>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default VerifyToken;
